import { useContext, useEffect, useState } from 'react'

import { GenderOptionControl } from '@hhgtech/hhg-components'
import {
  Button,
  Input,
  Text,
  Radio,
  Checkbox,
  CustomStylesType,
  ErrorLabel,
  TemplateProvider,
} from '@hhgtech/hhg-components/mantine'
import { LOCALE } from '@hhgtech/hhg-components/types'
import { Box, rem, Flex } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { AgeInput } from 'components/atoms/healthTools/form/ageInput'
import { CheckYourSelfSwitching } from 'components/atoms/healthTools/form/checkYourSelfSwitching'
import { StyledFormControl } from 'components/organisms/babyGrowthForm/index.styled'
import { getBmiAdditionalQNA } from 'components/pages/healthTools/bmi/helpers'
import { useCheckingYourself } from 'hooks/healthTools/useCheckingYourself'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT } from 'interfaces/constants'
import { ACTIVITY_LEVEL_NAME } from 'interfaces/constants/healthTools'
import { AppContext } from 'state/appContext'
import {
  formartSSOGenderToHealthtoolForm,
  checkAndSaveUserInfo,
} from 'utils/healthTools'

import * as BmiFormHelpers from './index.helpers'
import useStyles from './index.styles'
import { GenderOptionSelectControl } from '../genderOptionSelectControl'

const BmiForm = ({
  onSubmit: onSubmitProps,
  // template = 'embed',
  className,
  ...rest
}: BmiFormHelpers.BmiFormProps) => {
  const { classes, cx } = useStyles(undefined, {
    name: 'BmiForm',
  })
  const {
    state: { userInfo, isLoggedIn },
  } = useContext(AppContext)
  const { locale, router } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const [loading, setLoading] = useState(false)
  const {
    isCheckYourself,
    setIsCheckYourself,
    checkAvailableGenderOpt,
  } = useCheckingYourself()
  const form = useForm<BmiFormHelpers.BmiFormValue>({
    validate: yupResolver(BmiFormHelpers.generateSchema(f, locale)),
    initialValues: {
      height: '',
      weight: '',
      age: '',
      gender: '',
      question1: '',
      question2: '',
      newsletter: true,
    },
  })

  const currentUrl = useCurrentUrl()
  const bmiGenerated = BmiFormHelpers.generateBmiRules({ f, locale })
  const bmiUnit = BmiFormHelpers.bmiUnitValidation[locale]
  const isMobile = useIsMobile()

  const onValid = async (data: BmiFormHelpers.BmiFormValue) => {
    const height = bmiUnit.heightConvert(Number(data.height))
    const weight = bmiUnit.weightConvert(Number(data.weight))
    const {
      age = '',
      activityLevel = ACTIVITY_LEVEL_NAME.LOW_INTENSITY,
      gender,
    } = data

    const submitValue: BmiFormHelpers.BmiFormValue = {
      gender: data.gender,
      age: data.age,
      height: height.toString(),
      weight: weight.toString(),
      bmi: await BmiFormHelpers.getBmi({
        height,
        weight,
        age,
        gender,
        activityLevel,
      }),
      isCheckYourself,
      activityLevel: data.activityLevel,
      additional_questions: getBmiAdditionalQNA(f, data),
    }

    if (userInfo?.id && isCheckYourself) {
      setLoading(true)
      await checkAndSaveUserInfo({ userInfo, data })
      setLoading(false)
    }

    if (!userInfo?.id) {
      BmiFormHelpers.syncParamsToUseOnSSOForm(
        {
          gender: data.gender,
          birthday: data.birthday,
        },
        router,
      )
    }

    onSubmitProps?.(submitValue)
  }
  useEffect(() => {
    if (isCheckYourself) {
      userInfo?.gender !== -1 &&
        form.setFieldValue(
          'gender',
          formartSSOGenderToHealthtoolForm(userInfo?.gender || 0),
        )
      if (userInfo?.birthday) {
        form.setFieldValue(
          'birthday',
          dayjs(userInfo?.birthday, API_DATE_FORMAT).toDate(),
        )
        form.setFieldValue(
          'age',
          String(
            dayjs().diff(dayjs(userInfo?.birthday, API_DATE_FORMAT), 'year'),
          ),
        )
      }

      userInfo?.height && form.setFieldValue('height', userInfo?.height)
      userInfo?.weight && form.setFieldValue('weight', userInfo?.weight)
      form.setFieldValue(
        'activityLevel',
        userInfo?.activity_level || ACTIVITY_LEVEL_NAME.LOW_INTENSITY,
      )
    } else {
      form.reset()
    }
  }, [isCheckYourself, userInfo])

  useEffect(() => {
    isLoggedIn && setIsCheckYourself(true)
  }, [isLoggedIn])

  return (
    <TemplateProvider variant={CustomStylesType.LEADGEN}>
      <Box className={cx(className, classes.root)} {...rest}>
        <form className={classes.form} onSubmit={form.onSubmit(onValid)}>
          <Box className={cx('age-group', classes.group)}>
            {isLoggedIn && (
              <StyledFormControl className={cx('age-item', classes.item)}>
                <CheckYourSelfSwitching
                  checked={isCheckYourself}
                  onChange={(e) => setIsCheckYourself(e.currentTarget.checked)}
                />
              </StyledFormControl>
            )}
            <StyledFormControl className={cx('age-item', classes.item)}>
              <AgeInput
                {...form.getInputProps('birthday')}
                disableSSOAge={!isCheckYourself}
                onChange={(v) => {
                  form.setFieldValue('age', String(dayjs().diff(v, 'year')))
                  form.setFieldValue('birthday', v as Date)
                }}
              />
            </StyledFormControl>
          </Box>
          <Box className={classes.group}>
            <Box className={cx('gender', classes.item)}>
              {isMobile ? (
                <GenderOptionControl
                  label={bmiGenerated.gender?.label}
                  availableValue={checkAvailableGenderOpt(
                    form.getInputProps('gender').value,
                  )}
                  {...form.getInputProps('gender')}
                  styles={{
                    wrapper: {
                      gap: rem(16),
                    },
                  }}
                />
              ) : (
                <GenderOptionSelectControl
                  label={bmiGenerated.gender?.label}
                  availableValue={checkAvailableGenderOpt(
                    form.getInputProps('gender').value,
                  )}
                  {...form.getInputProps('gender')}
                  styles={{
                    wrapper: {
                      gap: rem(16),
                    },
                  }}
                />
              )}
            </Box>
            <Box className={classes.item}>
              <Input.Number
                label={
                  <Text size="s4" as="label">
                    {bmiGenerated.height?.label}
                  </Text>
                }
                name="height"
                {...{
                  ...form.getInputProps('height'),
                  value: form.getInputProps('height').value
                    ? Number(form.getInputProps('height').value)
                    : '',
                }}
                min={0}
                precision={1}
                inputMode="decimal"
                rightSection={f({
                  id: 'bmi.heightInput.desc',
                  defaultMessage: '(cm)',
                })}
                rightSectionWidth={60}
                classNames={{
                  rightSection: classes.inputRightSection,
                }}
                placeholder={f(
                  { id: 'tools.inputPlaceholder' },
                  { content: locale === LOCALE.Myanmar ? 6 : 170 },
                )}
              />
            </Box>
            <Box className={classes.item}>
              <Input.Number
                label={
                  <Text size="s4" as="label">
                    {bmiGenerated.weight?.label}
                  </Text>
                }
                name="weight"
                {...{
                  ...form.getInputProps('weight'),
                  value: form.getInputProps('weight').value
                    ? Number(form.getInputProps('weight').value)
                    : '',
                }}
                precision={1}
                inputMode="decimal"
                min={0}
                rightSection={f({
                  id: 'bmi.weightInput.desc',
                  defaultMessage: '(kg)',
                })}
                rightSectionWidth={60}
                classNames={{
                  rightSection: classes.inputRightSection,
                }}
                placeholder={f(
                  { id: 'tools.inputPlaceholder' },
                  { content: locale === LOCALE.Myanmar ? 130 : 60 },
                )}
              />
            </Box>
          </Box>
          <Box className={cx('question', classes.group)}>
            <Radio.Group
              className={cx('question1', classes.item)}
              name="question1"
              sx={{ flex: 1 }}
              label={
                <Text size="s4" weight="semiBold" span>
                  {f({
                    id: 'bmi.question.weightLossSupport',
                    defaultMessage:
                      'Are you interested in weight-loss treatment/support?',
                  })}
                </Text>
              }
              size="lg"
              {...form.getInputProps('question1')}
              error={
                form.getInputProps('question1').error && (
                  <ErrorLabel label={form.getInputProps('question1').error} />
                )
              }
            >
              <Flex gap="lg" sx={{ paddingTop: rem(8) }}>
                <Radio
                  size="sm"
                  value="1"
                  label={f({
                    id: 'bmi.answer.yes',
                    defaultMessage: 'Yes',
                  })}
                />
                <Radio
                  size="sm"
                  value="0"
                  label={f({
                    id: 'bmi.answer.no',
                    defaultMessage: 'No',
                  })}
                />
              </Flex>
            </Radio.Group>
            <Radio.Group
              className={cx('question2', classes.item)}
              name="question2"
              sx={{ flex: 1 }}
              label={
                <Text size="s4" weight="semiBold" span>
                  {f({
                    id: 'bmi.question.glp1Awareness',
                    defaultMessage: 'Are you aware of GLP-1 treatment?',
                  })}
                </Text>
              }
              size="lg"
              description={
                <Text style={{ fontSize: '10px' }} span>
                  {f({
                    id: 'bmi.question.glp1Awareness.desc',
                    defaultMessage:
                      'It’s a class of medications that can help with weight management by curbing appetite and slowing digestion',
                  })}
                </Text>
              }
              {...form.getInputProps('question2')}
              error={
                form.getInputProps('question2').error && (
                  <ErrorLabel label={form.getInputProps('question2').error} />
                )
              }
            >
              <Flex gap="lg" sx={{ paddingTop: rem(8) }}>
                <Radio
                  size="sm"
                  value="1"
                  label={f({
                    id: 'bmi.answer.yes',
                    defaultMessage: 'Yes',
                  })}
                />
                <Radio
                  size="sm"
                  value="0"
                  label={f({
                    id: 'bmi.answer.no',
                    defaultMessage: 'No',
                  })}
                />
              </Flex>
            </Radio.Group>
          </Box>

          <Box
            className={cx('bmi-newsletter', classes.group)}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Checkbox
              {...form.getInputProps('newsletter', { type: 'checkbox' })}
              size="sm"
              label={
                <Text size="p3">
                  {f({
                    id: 'bmi.newsletter.label',
                    defaultMessage:
                      'Stay in the loop, shed the weight: Get expert updates on weight loss treatments and support directly to your inbox',
                  })}
                </Text>
              }
              styles={{
                inner: {
                  height: 20,
                },
              }}
            />
            <Button
              loading={loading}
              size="md"
              type="submit"
              className={cx('bmi-btn bmi--btn-submit', classes.btn)}
              data-event-category="Health Tool"
              data-event-action="BMI"
              data-event-label={currentUrl}
            >
              {f({
                id: 'bmi.submit',
                defaultMessage: 'Calculate',
              })}
            </Button>
          </Box>
        </form>
      </Box>
    </TemplateProvider>
  )
}

export { BmiForm, BmiFormHelpers }
